import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";

import { PromoteCategoryDialog } from "@/components/ui/admin/taxonomy/PromoteCategoryDialog";
import {
  sortTaxonomyByLabel,
  sortTaxonomyBySlug,
  type SortDirection,
} from "@/core/services/taxonomy.sort";
import {
  buildArtisticPeriodGroups,
  type ArtisticPeriodGroup,
} from "@/core/services/taxonomy.artistic-periods";
import { createTaxonomyService } from "@/core/services/factory";
import { promotedTypeForTaxonomyItem } from "@/core/services/taxonomy.promoted";
import type { PromotedCategory } from "@/core/services/types/promoted-category.types";
import type {
  ArtisticPeriod,
  TaxonomyData,
  TaxonomyItem,
} from "@/core/services/types/taxonomy.types";
import { useTranslations } from "@/lib/useTranslations";

function sortArtisticPeriodGroups(
  groups: ArtisticPeriodGroup[],
  key: "label" | "slug",
  direction: SortDirection,
): ArtisticPeriodGroup[] {
  const sortItems =
    key === "label"
      ? (items: ArtisticPeriod[]) => sortTaxonomyByLabel(items, direction)
      : (items: ArtisticPeriod[]) => sortTaxonomyBySlug(items, direction);

  const sortedGroups = [...groups].sort((a, b) => {
    const cmp =
      key === "label"
        ? a.parent.label.localeCompare(b.parent.label, "fr")
        : a.parent.slug.localeCompare(b.parent.slug);
    return direction === "asc" ? cmp : -cmp;
  });

  return sortedGroups.map((group) => ({
    ...group,
    children: sortItems(group.children),
  }));
}

function toggleSort<K extends string>(
  current: { key: K; direction: SortDirection },
  key: K,
): { key: K; direction: SortDirection } {
  if (current.key === key) {
    return { key, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { key, direction: "asc" };
}

type TaxonomyTab =
  | "art-forms"
  | "cultural-areas"
  | "artistic-periods"
  | "exhibition-types";
type TaxonomyTabLabelKey =
  | "artForms"
  | "culturalAreas"
  | "artisticPeriods"
  | "exhibitionTypes";

const tabs: { id: TaxonomyTab; labelKey: TaxonomyTabLabelKey }[] = [
  { id: "art-forms", labelKey: "artForms" },
  { id: "cultural-areas", labelKey: "culturalAreas" },
  { id: "artistic-periods", labelKey: "artisticPeriods" },
  { id: "exhibition-types", labelKey: "exhibitionTypes" },
];

interface TaxonomyPanelProps {
  data: TaxonomyData;
  promotedCategories: PromotedCategory[];
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="px-4 py-8 text-center text-sm text-neutral-500">{message}</p>
  );
}

function SortableHeader<K extends string>({
  label,
  sortKey,
  activeSort,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: K;
  activeSort: { key: K; direction: SortDirection };
  onSort: (key: K) => void;
  align?: "left" | "right";
}) {
  const isActive = activeSort.key === sortKey;

  return (
    <th
      className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-whart-text ${
          align === "right" ? "ml-auto" : ""
        } ${isActive ? "text-whart-text" : "text-neutral-500"}`}
      >
        <span>{label}</span>
        <span className="inline-flex flex-col text-[9px] leading-[0.65]">
          <span
            className={
              isActive && activeSort.direction === "asc"
                ? "text-whart-dark"
                : "text-neutral-300"
            }
          >
            ▲
          </span>
          <span
            className={
              isActive && activeSort.direction === "desc"
                ? "text-whart-dark"
                : "text-neutral-300"
            }
          >
            ▼
          </span>
        </span>
      </button>
    </th>
  );
}

function OnboardingCheckbox({
  item,
  disabled,
  onToggle,
}: {
  item: TaxonomyItem;
  disabled: boolean;
  onToggle: (id: string, onboarding: boolean) => void;
}) {
  const t = useTranslations("taxonomy");

  return (
    <label className="inline-flex items-center gap-2 font-medium text-whart-text">
      <input
        type="checkbox"
        checked={item.onboarding}
        disabled={disabled}
        onChange={(event) => onToggle(item.id, event.target.checked)}
        className="size-4 rounded border border-black/10"
        aria-label={`${t("onboarding")} — ${item.label}`}
        title={t("onboardingHint")}
      />
    </label>
  );
}

function findPromotedEntry(
  item: TaxonomyItem,
  promoted: PromotedCategory[],
): PromotedCategory | null {
  const type = promotedTypeForTaxonomyItem(item);
  if (!type) return null;
  return (
    promoted.find(
      (row) => row.type === type && row.category === item.label,
    ) ?? null
  );
}

function PromotedStatusCell({
  item,
  promotedCategories,
  onPromote,
}: {
  item: TaxonomyItem;
  promotedCategories: PromotedCategory[];
  onPromote: (item: TaxonomyItem) => void;
}) {
  const t = useTranslations("taxonomy");
  const entry = findPromotedEntry(item, promotedCategories);

  if (entry) {
    return (
      <span
        className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-zinc-50 px-2.5 py-1 text-xs font-medium text-whart-text"
        title={t("promoted.alreadyPromoted")}
      >
        {entry.img ? (
          <img
            src={entry.img}
            alt=""
            width={16}
            height={16}
            className="size-4 shrink-0"
          />
        ) : null}
        {t("promoted.alreadyPromoted")}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onPromote(item)}
      className="rounded-xl border border-black/10 bg-yellow-100 px-2.5 py-1 text-xs font-medium text-zinc-900 transition-colors hover:bg-yellow-200"
    >
      {t("promote")}
    </button>
  );
}

function FlatTaxonomyTable({
  items,
  emptyMessage,
  showOnboarding,
  showPromote,
  promotedCategories,
  pendingId,
  onToggleOnboarding,
  onPromote,
}: {
  items: TaxonomyItem[];
  emptyMessage: string;
  showOnboarding?: boolean;
  showPromote?: boolean;
  promotedCategories?: PromotedCategory[];
  pendingId?: string | null;
  onToggleOnboarding?: (id: string, onboarding: boolean) => void;
  onPromote?: (item: TaxonomyItem) => void;
}) {
  const t = useTranslations("taxonomy");
  const tCommon = useTranslations("common");
  const [sort, setSort] = useState<{
    key: "label" | "slug";
    direction: SortDirection;
  }>({ key: "label", direction: "asc" });

  const sortedItems = useMemo(() => {
    if (sort.key === "slug") {
      return sortTaxonomyBySlug(items, sort.direction);
    }
    return sortTaxonomyByLabel(items, sort.direction);
  }, [items, sort]);

  if (items.length === 0) {
    return <EmptyState message={emptyMessage} />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[28rem] table-fixed text-left text-sm">
        <colgroup>
          <col />
          <col className="w-48" />
          {showOnboarding ? <col className="w-28" /> : null}
          {showPromote ? <col className="w-32" /> : null}
        </colgroup>
        <thead>
          <tr className="wireframe-border-b bg-neutral-50 text-xs font-medium">
            <SortableHeader
              label={tCommon("name")}
              sortKey="label"
              activeSort={sort}
              onSort={(key) => setSort((current) => toggleSort(current, key))}
            />
            <SortableHeader
              label={tCommon("slug")}
              sortKey="slug"
              activeSort={sort}
              onSort={(key) => setSort((current) => toggleSort(current, key))}
            />
            {showOnboarding ? (
              <th className="px-4 py-3 text-left uppercase tracking-wider text-neutral-500">
                {t("onboarding")}
              </th>
            ) : null}
            {showPromote ? (
              <th className="px-4 py-3 text-left uppercase tracking-wider text-neutral-500">
                {t("promoted.title")}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {sortedItems.map((item) => (
            <tr key={item.id} className="hover:bg-neutral-50">
              <td className="px-4 py-3 font-medium text-whart-text">
                {item.label}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-neutral-600">
                {item.slug}
              </td>
              {showOnboarding && onToggleOnboarding ? (
                <td className="px-4 py-3">
                  <OnboardingCheckbox
                    item={item}
                    disabled={pendingId === item.id}
                    onToggle={onToggleOnboarding}
                  />
                </td>
              ) : null}
              {showPromote && onPromote && promotedCategories ? (
                <td className="px-4 py-3">
                  <PromotedStatusCell
                    item={item}
                    promotedCategories={promotedCategories}
                    onPromote={onPromote}
                  />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TaxonomyPanel({
  data,
  promotedCategories,
}: TaxonomyPanelProps) {
  const t = useTranslations("taxonomy");
  const tCommon = useTranslations("common");
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TaxonomyTab>("art-forms");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [promoteItem, setPromoteItem] = useState<TaxonomyItem | null>(null);
  const [artisticPeriodSort, setArtisticPeriodSort] = useState<{
    key: "label" | "slug";
    direction: SortDirection;
  }>({ key: "label", direction: "asc" });

  const onboardingMutation = useMutation({
    mutationFn: async ({
      id,
      onboarding,
    }: {
      id: string;
      onboarding: boolean;
    }) => createTaxonomyService().updateOnboarding(id, onboarding),
    onMutate: async ({ id, onboarding }) => {
      setErrorMessage(null);
      await queryClient.cancelQueries({ queryKey: ["taxonomy"] });
      const previous = queryClient.getQueryData<TaxonomyData>(["taxonomy"]);

      queryClient.setQueryData<TaxonomyData>(["taxonomy"], (current) => {
        if (!current) return current;
        const patch = (items: TaxonomyItem[]) =>
          items.map((item) =>
            item.id === id ? { ...item, onboarding } : item,
          );
        return {
          artForms: patch(current.artForms),
          culturalAreas: current.culturalAreas,
          artisticPeriods: patch(current.artisticPeriods),
          exhibitionTypes: current.exhibitionTypes,
        };
      });

      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["taxonomy"], context.previous);
      }
      setErrorMessage(t("onboardingUpdateError"));
    },
    onSuccess: (result, _vars, context) => {
      if (!result.success) {
        if (context?.previous) {
          queryClient.setQueryData(["taxonomy"], context.previous);
        }
        setErrorMessage(result.error || t("onboardingUpdateError"));
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["taxonomy"] });
    },
  });

  const artisticPeriodGroups = useMemo(
    () =>
      sortArtisticPeriodGroups(
        buildArtisticPeriodGroups(data.artisticPeriods),
        artisticPeriodSort.key,
        artisticPeriodSort.direction,
      ),
    [data.artisticPeriods, artisticPeriodSort],
  );

  const tabCounts: Record<TaxonomyTab, number> = {
    "art-forms": data.artForms.length,
    "cultural-areas": data.culturalAreas.length,
    "artistic-periods": data.artisticPeriods.length,
    "exhibition-types": data.exhibitionTypes.length,
  };

  const handleToggleOnboarding = (id: string, onboarding: boolean) => {
    onboardingMutation.mutate({ id, onboarding });
  };

  return (
    <section className="wireframe-border bg-whart-bg">
      <nav
        aria-label={t("categoriesNav")}
        className="border-b border-black/10"
      >
        <ul className="flex items-center gap-1 overflow-x-auto px-4">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;

            return (
              <li key={tab.id} className="shrink-0">
                <button
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`border-b-2 px-3 py-3 text-sm transition-colors ${
                    isActive
                      ? "border-whart-dark font-medium text-whart-text"
                      : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
                  }`}
                >
                  {t(`tabs.${tab.labelKey}`)}
                  <span className="ml-1.5 text-neutral-400">
                    ({tabCounts[tab.id]})
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {errorMessage ? (
        <p className="border-b border-black/10 px-4 py-2 text-sm text-red-600">
          {errorMessage}
        </p>
      ) : null}

      {activeTab === "art-forms" && (
        <FlatTaxonomyTable
          items={data.artForms}
          emptyMessage={t("empty.artForms")}
          showOnboarding
          showPromote
          promotedCategories={promotedCategories}
          pendingId={onboardingMutation.isPending ? onboardingMutation.variables?.id : null}
          onToggleOnboarding={handleToggleOnboarding}
          onPromote={setPromoteItem}
        />
      )}

      {activeTab === "cultural-areas" && (
        <FlatTaxonomyTable
          items={data.culturalAreas}
          emptyMessage={t("empty.culturalAreas")}
        />
      )}

      {activeTab === "exhibition-types" && (
        <FlatTaxonomyTable
          items={data.exhibitionTypes}
          emptyMessage={t("empty.exhibitionTypes")}
        />
      )}

      {activeTab === "artistic-periods" && (
        <div className="overflow-x-auto">
          {data.artisticPeriods.length === 0 ? (
            <EmptyState message={t("empty.artisticPeriods")} />
          ) : (
            <table className="w-full min-w-[28rem] table-fixed text-left text-sm">
              <colgroup>
                <col />
                <col className="w-48" />
                <col className="w-28" />
                <col className="w-32" />
              </colgroup>
              <thead>
                <tr className="wireframe-border-b bg-neutral-50 text-xs font-medium">
                  <SortableHeader
                    label={tCommon("name")}
                    sortKey="label"
                    activeSort={artisticPeriodSort}
                    onSort={(key) =>
                      setArtisticPeriodSort((current) =>
                        toggleSort(current, key),
                      )
                    }
                  />
                  <SortableHeader
                    label={tCommon("slug")}
                    sortKey="slug"
                    activeSort={artisticPeriodSort}
                    onSort={(key) =>
                      setArtisticPeriodSort((current) =>
                        toggleSort(current, key),
                      )
                    }
                    align="right"
                  />
                  <th className="px-4 py-3 text-left uppercase tracking-wider text-neutral-500">
                    {t("onboarding")}
                  </th>
                  <th className="px-4 py-3 text-left uppercase tracking-wider text-neutral-500">
                    {t("promoted.title")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {artisticPeriodGroups.map((group) => (
                  <Fragment key={group.parent.id}>
                    <tr className="bg-neutral-50">
                      <td className="px-4 py-3">
                        <span className="font-semibold text-whart-text">
                          {group.parent.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-neutral-500">
                        {group.parent.slug}
                      </td>
                      <td className="px-4 py-3">
                        <OnboardingCheckbox
                          item={group.parent}
                          disabled={
                            onboardingMutation.isPending &&
                            onboardingMutation.variables?.id === group.parent.id
                          }
                          onToggle={handleToggleOnboarding}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <PromotedStatusCell
                          item={group.parent}
                          promotedCategories={promotedCategories}
                          onPromote={setPromoteItem}
                        />
                      </td>
                    </tr>
                    {group.children.length === 0 ? (
                      <tr key={`${group.parent.id}-empty`}>
                        <td
                          colSpan={4}
                          className="px-4 py-3 pl-8 text-sm text-neutral-500"
                        >
                          {t("empty.subPeriods")}
                        </td>
                      </tr>
                    ) : (
                      group.children.map((child) => (
                        <tr key={child.id} className="hover:bg-neutral-50">
                          <td className="px-4 py-2.5 pl-8 font-medium text-whart-text">
                            {child.label}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-neutral-600">
                            {child.slug}
                          </td>
                          <td className="px-4 py-2.5">
                            <OnboardingCheckbox
                              item={child}
                              disabled={
                                onboardingMutation.isPending &&
                                onboardingMutation.variables?.id === child.id
                              }
                              onToggle={handleToggleOnboarding}
                            />
                          </td>
                          <td className="px-4 py-2.5">
                            <PromotedStatusCell
                              item={child}
                              promotedCategories={promotedCategories}
                              onPromote={setPromoteItem}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <PromoteCategoryDialog
        open={promoteItem != null}
        item={promoteItem}
        existing={promotedCategories}
        onClose={() => setPromoteItem(null)}
        onSaved={(items) => {
          queryClient.setQueryData(["taxonomy-promoted"], items);
          void queryClient.invalidateQueries({ queryKey: ["taxonomy-promoted"] });
        }}
      />
    </section>
  );
}
